import _ from "lodash";
import { call, select } from "redux-saga/effects";

import { Api } from "@/utils";

import loadLightGroupSettings from "./load-light-group-settings";
import loadLightGroups from "./load-light-groups";
import loadSite from "./load-site";

const convertVertexToApiPoint = (
    { swapXY, floorSpaceLength },
    { lat, lng },
) => {
    if (swapXY) {
        return {
            x: -lat,
            y: -(-lng + floorSpaceLength),
        };
    }
    return { x: lng * -1, y: lat * -1 };
};

const convertPolygonToApiRegion = (
    { swapXY, floorSpaceLength },
    { vertices, zIndex },
) => {
    // Don't include the final vertex which is the same as the first vertex
    const path = vertices
        .slice(0, -1)
        .map((vertex) =>
            convertVertexToApiPoint({ swapXY, floorSpaceLength }, vertex),
        );
    return { path, zIndex: zIndex ?? 0 };
};

const convertPolygonsToApiRegion = (
    geometries,
    { swapXY, floorSpaceLength } = {},
) =>
    geometries.map((geometry) =>
        convertPolygonToApiRegion({ swapXY, floorSpaceLength }, geometry),
    );

const buildPutControlGroupsPayloads = (
    geometries,
    groups,
    conversionOptions = {},
) => {
    const geometriesByDisplayId = _.groupBy(geometries, "group.displayID");

    return groups.map((group) => {
        let region = [];

        if (Object.hasOwn(geometriesByDisplayId, group.displayID)) {
            region = [
                ...region,
                ...convertPolygonsToApiRegion(
                    geometriesByDisplayId[group.displayID],
                    conversionOptions,
                ),
            ];
        }

        return { ...group, region };
    });
};

/**
 * Persists light group region geometries to the API for the given site.
 *
 * @param root0 - The action payload.
 * @param root0.siteId - The site ID to save groups for.
 * @param root0.groupGeometries - The group region geometries to save.
 * @param root0.lightGroups - The light groups metadata.
 * @yields Calls the API to persist light group geometries.
 */
function* saveLightGroups({ siteId, groupGeometries, lightGroups }) {
    try {
        const { controlGroupsJsonD2CEnabled } = yield call(loadSite, {
            siteId,
        });

        if (lightGroups.length === 0 || controlGroupsJsonD2CEnabled) {
            return;
        }

        const settings = yield select(({ map }) => map.general.settings);
        const { swapXY } = settings.spaceDetails;

        const floorSpaceWidth =
            parseFloat(settings.floorSpaceInfo.floorSpaceWidth) || 0;

        const floorSpaceLength =
            parseFloat(settings.floorSpaceInfo.floorSpaceLength) || 0;

        const payload = buildPutControlGroupsPayloads(
            groupGeometries,
            lightGroups,
            { swapXY, floorSpaceWidth, floorSpaceLength },
        );

        yield call(
            Api.put,
            "v1/control-groups/with-region-in-meters/",
            payload,
        );

        const newLightGroups = yield call(loadLightGroups, {
            swapXY,
            floorSpaceLength,
        });

        const defaultLightGroups = yield select(
            ({ map }) => map.general.defaultLightGroups,
        );

        yield call(loadLightGroupSettings, {
            lightGroups: newLightGroups,
            defaultLightGroups,
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log("error saving light groups: ", err);
    }
}

export default saveLightGroups;
